import type * as React from "react";

import { cn } from "@/lib/utils";

export type RowColumn<T> = {
  key: string;
  header: string;
  cell: (row: T) => React.ReactNode;
  /** Extra classes for both the header and the cells, such as `hidden md:table-cell`. */
  className?: string;
};

/** Optional checkboxes for acting on several rows at once. */
export type RowChecks<T> = {
  checked: ReadonlySet<string>;
  onChange: (checked: ReadonlySet<string>) => void;
  /** The accessible name of a row's checkbox. */
  label: (row: T) => string;
};

const CHECKBOX = "size-4 accent-[var(--primary)]";

function CheckAll<T>({
  rows,
  rowKey,
  checks,
}: {
  rows: readonly T[];
  rowKey: (row: T) => string;
  checks: RowChecks<T>;
}) {
  const all = rows.length > 0 && rows.every((row) => checks.checked.has(rowKey(row)));
  return (
    <th scope="col" className="w-10 py-3.5 pl-6">
      <input
        type="checkbox"
        aria-label="Select every row"
        className={CHECKBOX}
        checked={all}
        disabled={rows.length === 0}
        onChange={() => {
          checks.onChange(new Set(all ? [] : rows.map(rowKey)));
        }}
      />
    </th>
  );
}

/**
 * ADR-0087: one table on one surface. Every row opens its details in a side sheet. The first cell holds a button named for the row, so keyboard
 * and screen-reader users open it the same way a mouse click anywhere on the row does. With `checks`, a
 * leading checkbox selects the row instead of opening it.
 */
export function RowTable<T>({
  label,
  columns,
  rows,
  rowKey,
  rowLabel,
  onOpen,
  selectedKey,
  empty,
  checks,
}: {
  label: string;
  columns: readonly RowColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => string;
  onOpen: (row: T) => void;
  selectedKey?: string | null;
  empty?: React.ReactNode;
  checks?: RowChecks<T> | undefined;
}) {
  return (
    <div className="overflow-x-auto rounded-xl bg-card shadow-card">
      <table aria-label={label} className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-muted">
            {checks ? <CheckAll rows={rows} rowKey={rowKey} checks={checks} /> : null}
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  "px-4 py-3.5 text-xs font-semibold text-muted-foreground first:pl-6 last:pr-6",
                  column.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={rowKey(row)}
              row={row}
              columns={columns}
              label={rowLabel(row)}
              selected={selectedKey === rowKey(row)}
              onOpen={onOpen}
              check={checks ? { key: rowKey(row), checks } : undefined}
            />
          ))}
        </tbody>
      </table>
      {rows.length === 0 && empty ? (
        <div className="px-6 py-8 text-center text-muted-foreground">{empty}</div>
      ) : null}
    </div>
  );
}

function TableRow<T>({
  row,
  columns,
  label,
  selected,
  onOpen,
  check,
}: {
  row: T;
  columns: readonly RowColumn<T>[];
  label: string;
  selected: boolean;
  onOpen: (row: T) => void;
  check: { key: string; checks: RowChecks<T> } | undefined;
}) {
  const [first, ...rest] = columns as [RowColumn<T>, ...RowColumn<T>[]];
  const open = () => {
    onOpen(row);
  };
  return (
    <tr
      data-selected={selected ? "" : undefined}
      onClick={open}
      className="cursor-pointer border-b border-muted last:border-b-0 hover:bg-muted/60 data-selected:bg-info-background"
    >
      {check ? <RowCheck row={row} check={check} /> : null}
      <td className={cn("py-3.5 pr-4 align-middle", check ? "pl-4" : "pl-6", first.className)}>
        <button
          type="button"
          aria-label={label}
          className="block min-h-11 w-full rounded-md text-left outline-none focus-visible:shadow-[var(--focus-ring)]"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {first.cell(row)}
        </button>
      </td>
      {rest.map((column) => (
        <td key={column.key} className={cn("px-4 py-3.5 align-middle last:pr-6", column.className)}>
          {column.cell(row)}
        </td>
      ))}
    </tr>
  );
}

function RowCheck<T>({ row, check }: { row: T; check: { key: string; checks: RowChecks<T> } }) {
  const { key, checks } = check;
  return (
    <td
      className="w-10 py-3.5 pl-6 align-middle"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <input
        type="checkbox"
        aria-label={checks.label(row)}
        className={CHECKBOX}
        checked={checks.checked.has(key)}
        onChange={(event) => {
          const next = new Set(checks.checked);
          if (event.currentTarget.checked) next.add(key);
          else next.delete(key);
          checks.onChange(next);
        }}
      />
    </td>
  );
}

/** Primary text with a quieter second line, the standard first-column cell. */
export function CellTitle({ title, detail }: { title: React.ReactNode; detail?: React.ReactNode }) {
  return (
    <span className="block">
      <span className="block font-semibold text-foreground">{title}</span>
      {detail ? <span className="block text-sm text-muted-foreground">{detail}</span> : null}
    </span>
  );
}
